import React, { useEffect, useState } from "react";
import { Key, Plus, Copy, Trash2, Check, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "./ui/dialog";
import { useConfirm } from "./ConfirmDialog";
import { api } from "../lib/api";
import { toast } from "sonner";

const SCOPE_LABELS = {
  "read:products": "Lecture des produits (SKU, nom, catégorie, unité)",
  "read:vending_public": "Lecture contenu distributeurs (slot, qté restante, prix public)",
};

const AVAILABLE_SCOPES = [
  {
    key: "read:products",
    label: "Lecture des produits",
    hint: "SKU, nom, catégorie, unité — pour un service qui doit remplir une dropdown produit (ex : food-calc-14).",
  },
  {
    key: "read:vending_public",
    label: "Lecture contenu distributeurs",
    hint: "Slot, nom produit, quantité restante et prix public — pour publier l'état des distributeurs sur ton site web.",
  },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" });
  } catch (_) {
    return iso;
  }
};

export default function ApiKeysManager() {
  const confirm = useConfirm();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState(null); // {name, key, key_preview, created_at}
  const [copied, setCopied] = useState(false);
  const [selectedScopes, setSelectedScopes] = useState(["read:products"]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api-keys");
      setKeys(data);
    } catch (e) {
      // 403 ou autre : page peut être non-admin
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createKey = async () => {
    if (!name.trim()) { toast.error("Nom obligatoire"); return; }
    if (selectedScopes.length === 0) { toast.error("Sélectionne au moins une permission"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/api-keys", { name: name.trim(), scopes: selectedScopes });
      setRevealed(data);
      setName("");
      setSelectedScopes(["read:products"]);
      setCreateOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setCreating(false);
    }
  };

  const toggleScope = (scope) => {
    setSelectedScopes((prev) => prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]);
  };

  const copy = async () => {
    if (!revealed?.key) return;
    try {
      await navigator.clipboard.writeText(revealed.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Clé copiée");
    } catch (_) {
      toast.error("Copie impossible — sélectionnez et copiez manuellement");
    }
  };

  const revoke = async (k) => {
    const ok = await confirm({
      title: `Révoquer la clé « ${k.name} » ?`,
      description: "Le service utilisant cette clé sera immédiatement déconnecté. Action irréversible (vous pourrez en générer une nouvelle).",
      confirmText: "Révoquer",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/api-keys/${k.id}`);
      toast.success("Clé révoquée");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-lg p-6" data-testid="api-keys-zone">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Key className="w-5 h-5 text-[#1F4E3D]" />
          <h2 className="font-heading text-xl font-medium">Clés API (intégrations externes)</h2>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" data-testid="create-api-key-btn">
              <Plus className="w-4 h-4 mr-1" /> Nouvelle clé
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Créer une clé API</DialogTitle>
              <DialogDescription>
                Génère une clé en lecture seule pour permettre à un service externe (ex: food-calc-14) d'accéder à la liste de vos produits.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="mb-1.5 block">Nom de la clé</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="site-web-restaurant"
                  data-testid="api-key-name-input"
                />
                <p className="text-xs text-[#575A57] mt-1.5">Nom descriptif du service qui va utiliser cette clé (ex : « site-web-restaurant », « food-calc-14 »).</p>
              </div>
              <div>
                <Label className="mb-1.5 block">Permissions accordées</Label>
                <div className="space-y-2">
                  {AVAILABLE_SCOPES.map((s) => (
                    <label
                      key={s.key}
                      className={`flex items-start gap-2 p-2.5 border rounded cursor-pointer transition-colors ${selectedScopes.includes(s.key) ? "border-[#1F4E3D] bg-[#F0F5F1]" : "border-[#E5E5E0] bg-white hover:bg-[#F9F9F6]"}`}
                      data-testid={`api-key-scope-${s.key.replace(/[:]/g, "-")}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedScopes.includes(s.key)}
                        onChange={() => toggleScope(s.key)}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-[#1A1C1A]">{s.label}</div>
                        <div className="text-xs text-[#575A57] mt-0.5">{s.hint}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-[#8C6B2F] mt-2">
                  ⚠️ Lecture seule dans tous les cas — aucune écriture possible. Aucun accès aux prix négociés B2B, contacts clients, factures ou HACCP.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>Annuler</Button>
              <Button onClick={createKey} disabled={creating || !name.trim()} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="confirm-create-api-key">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Générer la clé
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <p className="text-xs text-[#575A57] mb-4">
        Permettent à un service tiers (ex: <strong>food-calc-14</strong>, <strong>site-web-restaurant</strong>) d&apos;accéder en lecture seule
        aux données AgroManage strictement nécessaires. Chaque clé est révocable en un clic — <strong>copiez-la au moment
        de la création, elle ne sera plus jamais affichée</strong>.
      </p>

      {/* Reveal d'une clé fraîchement créée */}
      {revealed && (
        <div className="mb-4 bg-[#FBF4E9] border-2 border-[#E8D3A8] rounded-lg p-4" data-testid="revealed-key-banner">
          <div className="flex items-start gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-[#8C6B2F] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-[#1A1C1A]">Clé « {revealed.name} » créée</div>
              <div className="text-xs text-[#8C6B2F]">
                ⚠️ <strong>Copiez la clé maintenant.</strong> Elle ne sera plus jamais affichée — si vous la perdez, il faudra en générer une nouvelle.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <code className="flex-1 bg-white border border-[#E5E5E0] rounded px-3 py-2 font-mono text-sm break-all" data-testid="revealed-key-value">
              {revealed.key}
            </code>
            <Button size="sm" onClick={copy} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="copy-api-key-btn">
              {copied ? <><Check className="w-4 h-4 mr-1" /> Copié</> : <><Copy className="w-4 h-4 mr-1" /> Copier</>}
            </Button>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setRevealed(null)} className="mt-2 text-xs text-[#575A57]" data-testid="dismiss-revealed-key">
            J'ai copié la clé, fermer
          </Button>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-[#A3A6A3]">Chargement…</div>
      ) : keys.length === 0 ? (
        <div className="text-sm text-[#A3A6A3] italic border border-dashed border-[#E5E5E0] rounded p-4 text-center">
          Aucune clé API. Cliquez sur « Nouvelle clé » pour en créer une.
        </div>
      ) : (
        <div className="space-y-2">
          {keys.map((k) => (
            <div key={k.id} className={`flex items-center gap-3 p-3 border rounded ${k.revoked ? "border-[#E5E5E0] bg-[#F4F4F0] opacity-60" : "border-[#E5E5E0] bg-white"}`} data-testid={`api-key-row-${k.id}`}>
              <Key className={`w-4 h-4 flex-shrink-0 ${k.revoked ? "text-[#A3A6A3]" : "text-[#1F4E3D]"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#1A1C1A]">{k.name}</div>
                <div className="text-xs text-[#575A57] font-mono">{k.key_preview}</div>
                <div className="text-[10px] text-[#A3A6A3] mt-0.5">
                  Créée le {fmtDate(k.created_at)} · Dernière utilisation : {fmtDate(k.last_used_at)}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {(k.scopes || []).map((s) => (
                  <Badge key={s} variant="outline" className="text-[10px] bg-[#E8EEE9] text-[#1F4E3D] border-[#1F4E3D]/30">
                    {SCOPE_LABELS[s] || s}
                  </Badge>
                ))}
                {k.revoked ? (
                  <Badge className="bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4]">Révoquée</Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => revoke(k)}
                    className="h-8 w-8 text-[#C05C47]"
                    data-testid={`revoke-api-key-${k.id}`}
                    title="Révoquer cette clé"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
